import * as React from 'react'
import * as Reakit from 'reakit/Tooltip'

import { getExtendedDialogState } from './dialog'
import { Input, InputHTMLProps, InputProps } from './input'
import { Tooltip, TooltipColor, TooltipTrigger, useTooltipState } from './tooltip'

type TooltipInputProps = InputProps & InputHTMLProps & { isInteractive?: boolean }

export interface TooltipInputWrapperProps extends Pick<InputHTMLProps, 'name'> {
  children: React.ReactElement<TooltipInputProps>
  /**
   * Tooltip text content
   */
  tooltipContent?: React.ReactNode
  tooltipColor?: keyof typeof TooltipColor
  tooltipSettings?: Reakit.TooltipInitialState
}

function TooltipInputWrapper({
  children,
  tooltipContent,
  tooltipColor,
  tooltipSettings,
  name,
}: TooltipInputWrapperProps) {
  const tooltip = useTooltipState({ placement: 'bottom', ...tooltipSettings })
  const { isFullyClosed } = getExtendedDialogState(tooltip)

  return (
    <>
      <TooltipTrigger
        state={tooltip}
        tabIndex={-1}
        onMouseEnter={(ev) => ev.preventDefault()}
        onMouseLeave={(ev) => ev.preventDefault()}
      >
        {children}
      </TooltipTrigger>
      {tooltipContent && !isFullyClosed && (
        <Tooltip
          className="flex px-2.5"
          id={name}
          state={tooltip}
          color={tooltipColor}
          data-testid={name?.length ? `${name}-tooltip` : undefined}
        >
          {tooltipContent}
        </Tooltip>
      )}
    </>
  )
}

function TooltipInputComponent(
  {
    tooltipContent,
    tooltipColor,
    tooltipSettings,
    isInteractive = true,
    ...props
  }: TooltipInputProps & Omit<TooltipInputWrapperProps, 'children'>,
  ref: React.ForwardedRef<HTMLInputElement>
) {
  const input = <Input {...props} ref={ref} />

  if (!isInteractive) {
    return input
  }

  return (
    <TooltipInputWrapper
      name={props.name}
      tooltipContent={tooltipContent}
      tooltipColor={tooltipColor}
      tooltipSettings={tooltipSettings}
    >
      {input}
    </TooltipInputWrapper>
  )
}

export const TooltipInput = React.forwardRef(TooltipInputComponent)
